import { useRef, useCallback, useMemo, useEffect, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { RefreshCw, GitMerge, Trash2, SlidersHorizontal, X } from 'lucide-react';

const NODE_RADIUS = { source: 14, table: 9, domain: 12 };

const NODE_COLORS = {
  source:  { bg: '#ede9fe', border: '#7c3aed', text: '#5b21b6' },
  table:   { bg: '#dbeafe', border: '#2563eb', text: '#1d4ed8' },
  domain:  { bg: '#d1fae5', border: '#059669', text: '#065f46' },
};

const LINK_COLOR = {
  CONTAINS:   '#6366f1',
  BELONGS_TO: '#059669',
  SIMILAR_TO: '#f59e0b',
};

const DEFAULT_VIZ = {
  showArrows:    true,
  linkCurvature: 0.15,
  chargeStrength: -600,
  linkDistance:  120,
  nodeScale:     1.0,
};

const GraphViewer = ({
  fgData,
  rawGraph,
  loading,
  error,
  selectedNode,
  onNodeClick,
  onBackgroundClick,
  onNodeRemove,
  onRetry,
  search,
  nodesWithHiddenNeighbors,
}) => {
  const fgRef = useRef(null);
  const [showSettings, setShowSettings] = useState(false);
  const [vizSettings, setVizSettings] = useState(DEFAULT_VIZ);
  const [contextMenu, setContextMenu] = useState(null);
  const lastClickRef = useRef(null);

  const dragNodeStart = useRef(null);
  const dragNeighborStarts = useRef(new Map());

  const searchLower = (search || '').toLowerCase().trim();

  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    fg.d3Force('charge')?.strength(vizSettings.chargeStrength);
    fg.d3Force('link')?.distance(vizSettings.linkDistance).strength(0.6);
    fg.d3Force('center')?.strength(0.03);
  }, [fgData, vizSettings.chargeStrength, vizSettings.linkDistance]);

  useEffect(() => {
    if (!loading && fgData && fgData.nodes.length > 0) {
      setTimeout(() => fgRef.current?.zoomToFit(400, 60), 700);
    }
  }, [loading]);

  const handleNodeDrag = useCallback((node) => {
    if (!dragNodeStart.current) {
      dragNodeStart.current = { x: node.x, y: node.y };
      const neighbors = new Set();
      (fgData?.links || []).forEach((link) => {
        const srcId = typeof link.source === 'object' ? link.source?.id : link.source;
        const tgtId = typeof link.target === 'object' ? link.target?.id : link.target;
        if (srcId === node.id && link.target) neighbors.add(typeof link.target === 'object' ? link.target : (fgData?.nodes || []).find((n) => n.id === tgtId));
        if (tgtId === node.id && link.source) neighbors.add(typeof link.source === 'object' ? link.source : (fgData?.nodes || []).find((n) => n.id === srcId));
      });
      neighbors.delete(undefined);
      dragNeighborStarts.current = new Map();
      neighbors.forEach((nb) => {
        if (nb) dragNeighborStarts.current.set(nb, { startX: nb.fx ?? nb.x ?? 0, startY: nb.fy ?? nb.y ?? 0 });
      });
    }
    const dx = node.x - dragNodeStart.current.x;
    const dy = node.y - dragNodeStart.current.y;
    dragNeighborStarts.current.forEach((start, nb) => {
      nb.fx = start.startX + dx * 0.65;
      nb.fy = start.startY + dy * 0.65;
    });
  }, [fgData]);

  const handleNodeDragEnd = useCallback((node) => {
    node.fx = node.x;
    node.fy = node.y;
    dragNeighborStarts.current.forEach((_start, nb) => {
      nb.fx = nb.x ?? nb.fx;
      nb.fy = nb.y ?? nb.fy;
    });
    dragNodeStart.current = null;
    dragNeighborStarts.current = new Map();
  }, []);

  const paintNode = useCallback((node, ctx, globalScale) => {
    const colors = NODE_COLORS[node.type] ?? NODE_COLORS.source;
    const r = (NODE_RADIUS[node.type] ?? 9) * vizSettings.nodeScale;
    const dimmed = searchLower && !node.label?.toLowerCase().includes(searchLower);
    const isSelected = selectedNode && node.id === selectedNode.id;

    ctx.globalAlpha = dimmed ? 0.15 : 1;

    if (isSelected) {
      ctx.beginPath();
      ctx.arc(node.x, node.y, r + 5, 0, 2 * Math.PI);
      ctx.fillStyle = colors.border + '30';
      ctx.fill();
    }

    ctx.shadowColor = colors.border + '44';
    ctx.shadowBlur = 6;
    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
    ctx.fillStyle = colors.bg;
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = colors.border;
    ctx.lineWidth = isSelected ? 3 / globalScale : 2 / globalScale;
    ctx.stroke();

    const label = (node.label ?? node.id ?? '').toString();
    const fontSize = Math.max(4, 12 / globalScale);
    ctx.font = `600 ${fontSize}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = 3 / globalScale;
    ctx.lineJoin = 'round';
    ctx.strokeText(label, node.x, node.y + r + 3 / globalScale);
    ctx.fillStyle = colors.text;
    ctx.fillText(label, node.x, node.y + r + 3 / globalScale);

    if (!dimmed && nodesWithHiddenNeighbors && nodesWithHiddenNeighbors.has(node.id)) {
      const badgeR = Math.max(3, 5 / globalScale);
      const bx = node.x + r * 0.78;
      const by = node.y - r * 0.78;
      ctx.beginPath();
      ctx.arc(bx, by, badgeR, 0, 2 * Math.PI);
      ctx.fillStyle = '#6366f1';
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold ${Math.max(3, 7 / globalScale)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('+', bx, by);
    }

    ctx.globalAlpha = 1;
  }, [searchLower, selectedNode, nodesWithHiddenNeighbors, vizSettings.nodeScale]);

  const paintNodeArea = useCallback((node, color, ctx) => {
    const r = (NODE_RADIUS[node.type] ?? 9) + 4;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
    ctx.fill();
  }, []);

  return (
    <div className="flex-1 relative min-w-0 min-h-[420px] md:min-h-0 bg-slate-50">
      {loading && (
        <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-700">Loading graph...</p>
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="bg-white border border-red-200 rounded-xl p-6 flex items-center gap-3 max-w-sm shadow">
            <div className="text-red-500 font-bold text-lg">!</div>
            <div>
              <div className="font-semibold text-red-700">Failed to load graph</div>
              <div className="text-sm text-red-600 mt-1">{error}</div>
              <button onClick={onRetry} className="mt-3 text-sm text-red-700 underline">
                Try again
              </button>
            </div>
          </div>
        </div>
      )}
      {!loading && !error && fgData && fgData.nodes.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center max-w-sm shadow">
            <GitMerge className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <div className="font-semibold text-gray-700">Graph is empty</div>
            <div className="text-sm text-gray-500 mt-1">
              Connect a data source and run analysis to build the semantic graph.
            </div>
          </div>
        </div>
      )}

      {fgData && (
        <ForceGraph2D
          ref={fgRef}
          graphData={fgData}
          backgroundColor="#f8fafc"
          nodeCanvasObject={paintNode}
          nodeCanvasObjectMode={() => 'replace'}
          nodePointerAreaPaint={paintNodeArea}
          linkColor={(link) => LINK_COLOR[link.type] ?? '#94a3b8'}
          linkWidth={1.5}
          linkDirectionalArrowLength={vizSettings.showArrows ? 6 : 0}
          linkDirectionalArrowRelPos={1}
          linkCurvature={vizSettings.linkCurvature}
          onNodeClick={(node) => {
            const now = Date.now();
            const last = lastClickRef.current;
            if (last && last.id === node.id && now - last.time < 350) {
              lastClickRef.current = null;
            } else {
              lastClickRef.current = { id: node.id, time: now };
              onNodeClick && onNodeClick(node);
            }
          }}
          onBackgroundClick={() => {
            onBackgroundClick && onBackgroundClick();
            setContextMenu(null);
          }}
          onNodeRightClick={(node, event) => {
            event.preventDefault();
            setContextMenu({ node, x: event.clientX, y: event.clientY });
          }}
          onNodeDrag={handleNodeDrag}
          onNodeDragEnd={handleNodeDragEnd}
          onEngineStop={() => {
            if (fgData) {
              fgData.nodes.forEach((node) => {
                if (node.fx == null) { node.fx = node.x; node.fy = node.y; }
              });
            }
          }}
          warmupTicks={80}
          cooldownTicks={0}
          d3AlphaDecay={0.02}
          d3VelocityDecay={0.4}
        />
      )}

      {/* Settings toggle button */}
      <button
        onClick={() => setShowSettings(v => !v)}
        className={`absolute top-2 right-2 sm:top-3 sm:right-3 z-20 flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg border text-xs sm:text-sm transition-colors shadow-sm ${
          showSettings ? 'border-blue-400 bg-blue-50 text-primaryDark' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
        }`}
      >
        <SlidersHorizontal className="w-3.5 h-3.5" />
      </button>

      {/* Settings panel */}
      {showSettings && (
        <div className="absolute top-11 left-2 right-2 sm:left-auto sm:top-12 sm:right-3 z-20 bg-white border border-gray-200 rounded-xl shadow-xl w-auto sm:w-56 p-3 sm:p-4 space-y-4 text-sm max-w-[calc(100%-1rem)] sm:max-w-none">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-gray-700">Visualization</span>
            <button onClick={() => setShowSettings(false)} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Link Arrows</div>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <div
                onClick={() => setVizSettings(v => ({ ...v, showArrows: !v.showArrows }))}
                className={`w-9 h-5 rounded-full transition-colors ${vizSettings.showArrows ? 'bg-blue-500' : 'bg-gray-300'}`}
              >
                <div className={`w-4 h-4 bg-white rounded-full shadow mt-0.5 transition-transform ${vizSettings.showArrows ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </div>
              <span className="text-gray-600 text-xs">{vizSettings.showArrows ? 'Shown' : 'Hidden'}</span>
            </label>
          </div>

          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              Node Scale: {vizSettings.nodeScale.toFixed(1)}x
            </div>
            <input
              type="range" min="0.5" max="2" step="0.1"
              value={vizSettings.nodeScale}
              onChange={e => setVizSettings(v => ({ ...v, nodeScale: parseFloat(e.target.value) }))}
              className="w-full accent-blue-500"
            />
          </div>

          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              Charge: {vizSettings.chargeStrength}
            </div>
            <input
              type="range" min="-2000" max="-100" step="50"
              value={vizSettings.chargeStrength}
              onChange={e => setVizSettings(v => ({ ...v, chargeStrength: parseInt(e.target.value) }))}
              className="w-full accent-blue-500"
            />
          </div>

          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              Link Distance: {vizSettings.linkDistance}
            </div>
            <input
              type="range" min="30" max="300" step="10"
              value={vizSettings.linkDistance}
              onChange={e => setVizSettings(v => ({ ...v, linkDistance: parseInt(e.target.value) }))}
              className="w-full accent-blue-500"
            />
          </div>
        </div>
      )}

      {/* Right-click context menu */}
      {contextMenu && (
        <div
          className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl py-1 min-w-[160px]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onMouseLeave={() => setContextMenu(null)}
        >
          <div className="px-3 py-1.5 text-xs text-gray-400 border-b border-gray-100 truncate max-w-[200px]">
            {contextMenu.node.label ?? contextMenu.node.id}
          </div>
          <button
            onClick={() => {
              onNodeRemove && onNodeRemove(contextMenu.node.id);
              setContextMenu(null);
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Remove from view
          </button>
        </div>
      )}

      {/* Legend */}
      <div className="absolute top-2 left-2 sm:top-auto sm:bottom-4 sm:left-4 z-30 bg-white/90 border border-gray-200 rounded-lg px-2.5 sm:px-3 py-2 text-xs shadow-sm max-w-[calc(100%-1rem)] sm:max-w-none">
        <div className="font-semibold text-gray-500 uppercase tracking-wider mb-1.5 text-[10px]">Legend</div>
        {['source', 'table', 'domain'].map((type) => (
          <div key={type} className="flex items-center gap-2 mb-1">
            <div
              className="w-3 h-3 rounded-full border-2 shrink-0"
              style={{ background: NODE_COLORS[type].bg, borderColor: NODE_COLORS[type].border }}
            />
            <span className="text-gray-600 capitalize">{type}</span>
          </div>
        ))}
        <div className="flex items-center gap-2 mt-1.5 mb-1">
          <div className="w-5 h-0 border-t-2 shrink-0" style={{ borderColor: LINK_COLOR.CONTAINS }} />
          <span className="text-gray-500">Contains</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-5 h-0 border-t-2 shrink-0" style={{ borderColor: LINK_COLOR.BELONGS_TO }} />
          <span className="text-gray-500">Belongs To</span>
        </div>
      </div>
    </div>
  );
};

export default GraphViewer;
